import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { authFetch, clearAuthToken, getAuthToken, setAuthToken } from '@/api/client'

export interface User {
  user_id: string
  tenant_id: string | null
  tenant_name: string | null
  tenant_type: string | null
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  products: string[]
  email_verified: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(getAuthToken())
  const user = ref<User | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const hasVetReviews = computed(() => user.value?.products?.includes('vetreviews') ?? false)
  const isAdmin = computed(
    () => user.value?.role === 'admin' || user.value?.tenant_type === 'labeling_admin',
  )
  const displayName = computed(() => {
    const u = user.value
    if (!u) return ''
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ')
    return name || u.email
  })

  async function login(email: string, password: string): Promise<void> {
    const r = await authFetch<{ access_token: string }>('/auth/login', { email, password })
    setAuthToken(r.access_token)
    token.value = r.access_token
    await fetchMe()
  }

  async function fetchMe(): Promise<void> {
    if (!token.value) return
    loading.value = true
    try {
      user.value = await authFetch<User>('/auth/me')
    } catch {
      clear()
    } finally {
      loading.value = false
    }
  }

  function clear(): void {
    clearAuthToken()
    token.value = null
    user.value = null
  }

  function logout(): void {
    clear()
    window.location.assign('/login')
  }

  return {
    token, user, loading,
    isAuthenticated, hasVetReviews, isAdmin, displayName,
    login, fetchMe, clear, logout,
  }
})
